import type { QueryResponse } from "@unbox/api";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { streamQuery, streamReport } from "@/pages/ask/api/sse";
import { AskSurfaceContext } from "@/pages/ask/lib/ask-surface";
import { applySSEEvent } from "@/pages/ask/lib/chat-reducer";
import type { Brand } from "@/pages/ask/lib/data";
import { firstName, greeting, weekdayLabel } from "@/pages/ask/lib/greeting";
import { downloadChatTranscript } from "@/pages/ask/lib/transcript";
import { DrawerComposer, DrawerEmptyState, DrawerHeader, NoMemoryNotice } from "./drawer-chrome";
import GlossaryPanel from "./GlossaryPanel";
import Icon from "./Icon";
import MessageBubble, { type Message } from "./MessageBubble";
import PromptGrid from "./PromptGrid";
import { DateRangePicker, type ReportRequest, type TemplateMeta, useVisibleReportTemplates } from "./ReportPicker";
import { ScopeAvatar } from "./ScopeAvatar";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getUserInitials(email: string): string {
  const [local] = email.split("@");
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// AppHeader
// ---------------------------------------------------------------------------
function AppHeader({
  scope,
  onNewChat,
  hasThread,
  userInitials,
  onOpenGlossary,
}: {
  scope: Brand;
  onNewChat: () => void;
  hasThread: boolean;
  userInitials: string;
  onOpenGlossary: () => void;
}) {
  return (
    <header
      style={{
        height: 56,
        padding: "0 20px",
        borderBottom: "1px solid var(--color-border-secondary)",
        background: "#fff",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 12,
        position: "relative",
        zIndex: 10,
      }}
    >
      {/* Brand mark + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: "var(--color-brand-900)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width={13}
            height={13}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
          </svg>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--color-text-primary)",
              letterSpacing: "-0.01em",
            }}
          >
            Ask Unbox
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "0 6px",
              fontSize: 10,
              fontWeight: 500,
              lineHeight: "16px",
              borderRadius: 9999,
              background: "var(--color-brand-50)",
              color: "var(--color-brand-700)",
              border: "1px solid var(--color-brand-200)",
            }}
          >
            Alpha
          </span>
        </div>
      </div>

      <div style={{ flex: 1 }} />

      {/* Thread actions */}
      {hasThread && (
        <button
          onClick={onNewChat}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 500,
            color: "var(--color-text-secondary)",
            background: "#fff",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8,
            boxShadow: "var(--shadow-xs)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <Icon name="plus" size={14} color="currentColor" />
          New chat
        </button>
      )}

      {/* Current brand — set by the route, not selectable here */}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <ScopeAvatar initials={scope.initials} tone={scope.tone} size={26} />
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{scope.displayName}</span>
      </div>

      <div style={{ width: 1, height: 24, background: "var(--color-border-secondary)" }} />

      {/* Glossary */}
      <button
        onClick={onOpenGlossary}
        title="Metric glossary"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "none",
          border: "none",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-gray-400)",
          transition: "background 120ms, color 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--color-gray-100)";
          e.currentTarget.style.color = "var(--color-brand-700)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.color = "var(--color-gray-400)";
        }}
      >
        <Icon name="bookOpen" size={18} />
      </button>

      {/* User avatar */}
      <ScopeAvatar initials={userInitials} tone="brand" size={28} />
    </header>
  );
}

// ---------------------------------------------------------------------------
// EmptyHero — shown before any messages
// ---------------------------------------------------------------------------
function EmptyHero({
  scope,
  onPickPrompt,
  userName,
  userEmail,
  composer,
}: {
  scope: Brand;
  onPickPrompt: (text: string) => void;
  userName?: string;
  userEmail?: string;
  composer?: ReactNode;
}) {
  const now = new Date();
  const fname = firstName(userName, userEmail);

  return (
    <div
      style={{
        maxWidth: 980,
        margin: "0 auto",
        padding: "64px 32px 24px",
        width: "100%",
      }}
    >
      {/* Eyebrow + greeting */}
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "var(--color-brand-700)",
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 16,
          }}
        >
          <Icon name="sparkle" size={16} color="var(--color-brand-700)" />
          {weekdayLabel(now)} · here's what moved while you were away
        </div>

        <h1
          style={{
            fontSize: 40,
            lineHeight: "46px",
            letterSpacing: "-0.02em",
            fontWeight: 700,
            color: "var(--color-text-primary)",
            margin: 0,
          }}
        >
          {greeting(now)}
          {fname ? `, ${fname}` : ""}
        </h1>
      </div>

      {/* Ask bar — moves to the page bottom once a thread starts */}
      {composer && <div style={{ marginBottom: 32 }}>{composer}</div>}

      <PromptGrid scope={scope} onPickPrompt={onPickPrompt} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread
// ---------------------------------------------------------------------------
function Thread({
  messages,
  onExplore,
  onRephrase,
  disabled,
  dense,
  footer,
}: {
  messages: Message[];
  onExplore?: (question: string) => void;
  onRephrase?: (question: string) => void;
  disabled?: boolean;
  dense?: boolean;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(messages.length);

  // Keep the conversation pinned to the latest text — including while a response
  // streams in (content grows without the message count changing). On a new
  // message we always jump to the bottom; during streaming we only follow when
  // the user is already near the bottom, so scrolling up to read isn't interrupted.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const isNewMessage = messages.length !== prevCountRef.current;
    prevCountRef.current = messages.length;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (isNewMessage || nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    <div ref={ref} className={dense ? "ask-thread" : undefined} style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          padding: dense ? "0 0 8px" : "32px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: dense ? 16 : 32,
        }}
      >
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} onExplore={onExplore} onRephrase={onRephrase} disabled={disabled} />
        ))}
        {footer}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------
function Composer({
  onSubmit,
  onGenerateReport,
  scope,
  disabled,
  reportsDirection = "up",
  value,
  onValueChange,
  loading,
  onStop,
}: {
  onSubmit: (text: string) => void;
  onGenerateReport: (template: TemplateMeta, additionalInputs?: Record<string, string>) => void;
  scope: Brand;
  disabled: boolean;
  reportsDirection?: "up" | "down";
  /** Optional controlled value — used to seed the composer from outside (e.g. Rephrase). */
  value?: string;
  onValueChange?: (v: string) => void;
  loading?: boolean;
  onStop?: () => void;
}) {
  const [internalVal, setInternalVal] = useState("");
  const isControlled = value !== undefined && onValueChange !== undefined;
  const val = isControlled ? value : internalVal;
  const setVal = isControlled ? onValueChange : setInternalVal;

  const [reportsOpen, setReportsOpen] = useState(false);
  const reportTemplates = useVisibleReportTemplates();
  const [pendingDateRange, setPendingDateRange] = useState<TemplateMeta | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const reportsRef = useRef<HTMLDivElement>(null);

  // Close the reports popover on outside click
  const handleDocClick = useCallback((e: MouseEvent) => {
    if (reportsRef.current && !reportsRef.current.contains(e.target as Node)) {
      setReportsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (reportsOpen) {
      document.addEventListener("mousedown", handleDocClick);
    } else {
      document.removeEventListener("mousedown", handleDocClick);
    }
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [reportsOpen, handleDocClick]);

  useEffect(() => {
    if (!taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = Math.min(taRef.current.scrollHeight, 200) + "px";
  }, [val]);

  // ⌘K / Ctrl+K focuses the ask bar from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        taRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const send = () => {
    const text = val.trim();
    if (!text || disabled) return;
    onSubmit(text);
    setVal("");
    // If controlled, also clear internal (belt + suspenders in case consumer
    // lags a render behind — clears local textarea immediately)
    if (isControlled) setInternalVal("");
  };

  const canSend = val.trim().length > 0 && !disabled;
  const canReport = !disabled;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 820,
        margin: "0 auto",
        padding: "16px 24px 24px",
        position: "relative",
      }}
    >
      {pendingDateRange && (
        <DateRangePicker
          templateId={pendingDateRange.id}
          templateTitle={pendingDateRange.title}
          brandName={scope.displayName || scope.name}
          onConfirm={(startDate, endDate) => {
            setPendingDateRange(null);
            onGenerateReport(pendingDateRange, { start_date: startDate, end_date: endDate });
          }}
          onCancel={() => setPendingDateRange(null)}
        />
      )}

      {/* Reports popover — floats above the composer box */}
      {reportsOpen && (
        <div
          ref={reportsRef}
          style={{
            position: "absolute",
            ...(reportsDirection === "down" ? { top: "calc(100% - 16px)" } : { bottom: "calc(100% - 16px)" }),
            left: 24,
            right: 24,
            background: "#fff",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 12,
            boxShadow: "var(--shadow-md)",
            padding: "6px 0",
            zIndex: 50,
          }}
        >
          {/* Popover header */}
          <div
            style={{
              padding: "6px 14px 8px",
              borderBottom: "1px solid var(--color-border-tertiary)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="fileText" size={12} color="var(--color-brand-700)" strokeWidth={1.75} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--color-text-quaternary)",
              }}
            >
              Smart Reports
            </span>
          </div>

          {/* Template rows */}
          {reportTemplates.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setReportsOpen(false);
                if (t.requiresDateRange) {
                  setPendingDateRange(t);
                } else {
                  onGenerateReport(t);
                }
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
                transition: "background 80ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-gray-50)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  background: "var(--color-brand-50)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name={t.icon} size={13} color="var(--color-brand-700)" strokeWidth={1.75} />
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--color-text-primary)",
                }}
              >
                {t.title}
              </span>
              <span style={{ marginLeft: "auto", flexShrink: 0, display: "flex" }}>
                <Icon name="chevronRight" size={12} color="var(--color-text-quaternary)" strokeWidth={2} />
              </span>
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          background: "#fff",
          border: `1px solid ${disabled ? "var(--color-border-secondary)" : "var(--color-border-primary)"}`,
          borderRadius: 16,
          boxShadow: "var(--shadow-sm)",
          padding: "10px 10px 10px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          opacity: disabled ? 0.6 : 1,
          transition: "opacity 120ms",
        }}
      >
        {/* Gradient sparkle mark */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #7a5af8, #c026d3)",
            color: "#fff",
          }}
        >
          <Icon name="sparkle" size={16} color="#fff" />
        </div>
        <textarea
          ref={taRef}
          rows={1}
          value={val}
          disabled={disabled}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={
            disabled
              ? "Choose a brand or workspace to start…"
              : "Ask Unbox anything — creators, safety, GMV, content analysis…"
          }
          style={{
            border: "none",
            outline: "none",
            resize: "none",
            fontFamily: "inherit",
            fontSize: 15,
            lineHeight: "22px",
            color: "var(--color-text-primary)",
            background: "transparent",
            flex: 1,
            minWidth: 0,
            padding: "4px 0",
            minHeight: 24,
            maxHeight: 200,
          }}
        />

        {/* Reports trigger */}
        {canReport && (
          <button
            title="Generate a smart report"
            aria-label="Generate a smart report"
            aria-haspopup="dialog"
            aria-expanded={reportsOpen}
            onClick={() => setReportsOpen((v) => !v)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              flex: "0 0 auto",
              background: reportsOpen ? "var(--color-brand-50)" : "transparent",
              border: reportsOpen ? "1px solid var(--color-brand-200)" : "1px solid transparent",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: reportsOpen ? "var(--color-brand-700)" : "var(--color-gray-400)",
              transition: "background 120ms, color 120ms, border-color 120ms",
            }}
          >
            <Icon name="fileText" size={16} color="currentColor" strokeWidth={1.75} />
          </button>
        )}

        {/* ⌘K hint */}
        <span
          className="cmdk-hint"
          style={{
            flex: "0 0 auto",
            fontSize: 12,
            color: "var(--color-text-tertiary)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 6,
            padding: "3px 7px",
            background: "var(--color-bg-secondary)",
            fontFamily: "var(--font-mono)",
          }}
        >
          ⌘K
        </span>

        {/* Stop (while loading) / Send */}
        {loading && onStop ? (
          <button
            onClick={onStop}
            title="Stop"
            aria-label="Stop"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              flex: "0 0 auto",
              background: "var(--color-gray-100)",
              color: "var(--color-gray-700)",
              border: "1px solid var(--color-border-primary)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms",
              fontFamily: "inherit",
            }}
          >
            <div style={{ width: 10, height: 10, borderRadius: 2, background: "currentColor" }} />
          </button>
        ) : (
          <button
            onClick={send}
            disabled={!canSend}
            title="Send"
            aria-label="Send"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              flex: "0 0 auto",
              background: canSend ? "var(--color-brand-900)" : "var(--color-gray-100)",
              color: canSend ? "#fff" : "var(--color-gray-400)",
              border: `1px solid ${canSend ? "var(--color-brand-900)" : "var(--color-border-secondary)"}`,
              boxShadow: canSend ? "var(--shadow-skeuomorphic), var(--shadow-xs)" : "none",
              cursor: canSend ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms",
              fontFamily: "inherit",
            }}
          >
            <Icon name="arrowRight" size={18} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// sessionStorage persistence (T18)
// ---------------------------------------------------------------------------
const THREAD_STORAGE_KEY = "ask-unbox-active-thread";

// ---------------------------------------------------------------------------
// ChatInterface — root component
// ---------------------------------------------------------------------------
export default function ChatInterface({
  userEmail,
  userName,
  scope,
  variant = "page",
  initialQuestion,
  initialReport,
  suggestions,
  emptyStateTitle,
  onClose,
}: {
  userEmail: string;
  userName?: string;
  scope: Brand;
  variant?: "page" | "drawer";
  initialQuestion?: string;
  initialReport?: ReportRequest;
  suggestions?: string[];
  emptyStateTitle?: string;
  onClose?: () => void;
}) {
  const isDrawer = variant === "drawer";
  const drawerRedesign = useFeatureFlag("F-ask-drawer-redesign");
  const merchantScope = useScopedMerchantClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  // draft — lifted out of the docked Composer so "Rephrase" can seed it
  const [draft, setDraft] = useState("");
  // Stable session ID for the lifetime of this browser session — used to group
  // all queries from one user's chat session together in the Supabase log.
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  // The saved conversation this thread continues; null until the server creates one.
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Tracks whether the hydrate-on-mount effect has run, so the persist effect
  // never clobbers stored data with the empty initial state before restore runs.
  const hydratedRef = useRef(false);

  // Hydrate-on-mount: restore a saved thread from sessionStorage (runs once,
  // client-only). Must be in useEffect — not the useState initialiser — so
  // initial state is always empty and there is no hydration-mismatch risk.
  useEffect(() => {
    if (isDrawer) {
      hydratedRef.current = true;
      return;
    }
    try {
      const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(THREAD_STORAGE_KEY) : null;
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && Array.isArray(saved.messages) && saved.messages.length > 0) {
          setMessages(saved.messages);
          if (saved.sessionId) setSessionId(saved.sessionId);
          if (saved.conversationId) setConversationId(saved.conversationId);
        }
      }
    } catch {
      // Corrupt or unavailable storage — start fresh.
    }
    hydratedRef.current = true;
  }, [isDrawer]);

  // Persist effect: writes the thread to sessionStorage after every completed
  // turn. The streaming guard ensures we never persist a half-streamed message.
  useEffect(() => {
    if (isDrawer) return; // a drawer thread is never stored
    if (!hydratedRef.current) return; // don't clobber stored data before restore
    if (loading) return; // never persist a mid-stream state
    try {
      if (messages.length === 0) {
        window.sessionStorage.removeItem(THREAD_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(THREAD_STORAGE_KEY, JSON.stringify({ sessionId, conversationId, messages }));
      }
    } catch {
      // Quota exceeded or storage unavailable — ignore.
    }
  }, [messages, loading, sessionId, conversationId, isDrawer]);

  // Refs that mirror volatile state so submit (a stable useCallback) can read
  // the latest values without capturing them in its closure — safe because
  // submit only fires on user action, never mid-render.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const isDrawerRef = useRef(isDrawer);
  isDrawerRef.current = isDrawer;
  const merchantScopeRef = useRef(merchantScope);
  merchantScopeRef.current = merchantScope;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  // Abort controller for in-flight streaming queries (T7)
  const abortRef = useRef<AbortController | null>(null);

  const userInitials = getUserInitials(userEmail);
  const hasThread = messages.length > 0;
  const hasAnswer =
    !loading && messages.some((m) => m.role === "assistant" && (m.queryResponse != null || !!m.content));

  const submit = useCallback(async (question: string) => {
    if (!question.trim() || loadingRef.current) return;

    const currentScope = scopeRef.current;
    // A drawer question never continues an earlier one, so it always starts a new conversation.
    const continuing = isDrawerRef.current ? null : conversationIdRef.current;

    const thinkingId = crypto.randomUUID();
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: question.trim(),
      scope: currentScope,
    };
    const thinkingMsg: Message = {
      id: thinkingId,
      role: "assistant",
      content: "",
    };

    setMessages((prev) => [...prev, userMsg, thinkingMsg]);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamQuery(
        merchantScopeRef.current,
        {
          question: question.trim(),
          conversation_id: continuing,
          session_id: sessionId,
        },
        (event) => {
          if (event.event === "error") throw new Error(event.data.message);
          if (event.event === "conversation" && !isDrawerRef.current) {
            setConversationId(event.data.conversation_id);
          }
          setMessages((prev) =>
            applySSEEvent(prev, event, { msgId: thinkingId, question: question.trim(), sessionId }),
          );
        },
        controller.signal,
      );
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // User stopped — keep whatever streamed; mark the message stopped.
        setMessages((prev) =>
          prev.map((m) => (m.id === thinkingId ? { ...m, stopped: true, currentStepLabel: undefined } : m)),
        );
      } else {
        const errorPayload: QueryResponse = {
          steps: [],
          findings: [],
          explanation: "Something went wrong.",
          raw: "",
          sql: null,
          results: null,
          columns: [],
          row_count: 0,
          execution_ms: 0,
          definitions_applied: [],
          tables_referenced: [],
          window: null,
          notes: null,
          suggested_explorations: [],
          needs_clarification: false,
          error: err instanceof Error ? err.message : String(err),
          answer_query_index: null,
          visualizations: [],
        };
        setMessages((prev) =>
          prev.map((m) =>
            m.id === thinkingId
              ? { ...m, content: "", queryResponse: errorPayload, agentSteps: undefined, currentStepLabel: undefined }
              : m,
          ),
        );
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, []);

  // Cancel / stop an in-flight query (T7)
  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Rephrase — seeds the docked composer with the original question text so the
  // user can edit it before re-submitting. Only meaningful when a thread exists.
  const onRephrase = useCallback((q: string) => {
    setDraft(q);
  }, []);

  const generateReport = useCallback(
    async (template: TemplateMeta, additionalInputs?: Record<string, string>) => {
      if (loadingRef.current) return;
      const currentScope = scopeRef.current;
      const ms = merchantScopeRef.current;
      const templateId = template.id;

      // Human-readable label that includes the date range if supplied
      const dateLabel =
        additionalInputs?.start_date && additionalInputs?.end_date
          ? ` (${additionalInputs.start_date} to ${additionalInputs.end_date})`
          : "";
      const target = template.scope === "workspace" ? "" : ` for ${currentScope.displayName || currentScope.name}`;

      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: `Generate ${template.title}${target}${dateLabel}`,
        scope: currentScope,
      };
      const thinkingMsg: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        reportRun: true,
      };

      setMessages((prev) => [...prev, userMsg, thinkingMsg]);
      setLoading(true);

      try {
        await streamReport(ms, { templateId, session_id: sessionId, additionalInputs }, (event) => {
          if (event.type === "error") throw new Error(event.data.message);
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== thinkingMsg.id) return m;
              if (event.type === "progress") return { ...m, currentStepLabel: event.data.label, agentSteps: [] };
              if (event.type === "report") {
                return { ...m, currentStepLabel: undefined, agentSteps: undefined, generatedReport: event.data };
              }
              return m;
            }),
          );
        });
      } catch (err) {
        const errMsg: Message = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "",
          queryResponse: {
            steps: [],
            findings: [],
            explanation: "Could not generate the report.",
            raw: "",
            sql: null,
            results: null,
            columns: [],
            row_count: 0,
            execution_ms: 0,
            definitions_applied: [],
            tables_referenced: [],
            window: null,
            notes: null,
            suggested_explorations: [],
            needs_clarification: false,
            error: err instanceof Error ? err.message : String(err),
            answer_query_index: null,
            visualizations: [],
          },
        };
        setMessages((prev) => [...prev.slice(0, -1), errMsg]);
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  // A question or report handed in by the opener is run once per mount. Effects
  // run twice in development, and the ref keeps that from sending it twice.
  const askedInitialRef = useRef(false);
  useEffect(() => {
    if (askedInitialRef.current) return;
    askedInitialRef.current = true;
    if (initialReport) {
      generateReport(initialReport.template, initialReport.additionalInputs);
      return;
    }
    const question = initialQuestion?.trim();
    if (question) submit(question);
  }, [initialQuestion, initialReport, submit, generateReport]);

  function handleNewChat() {
    setMessages([]);
    setConversationId(null);
    setDraft("");
    try {
      window.sessionStorage.removeItem(THREAD_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  if (isDrawer) {
    return (
      <AskSurfaceContext.Provider value={drawerRedesign ? "drawer" : "page"}>
        <div className="ask-drawer-dark relative flex h-full min-h-0 w-full flex-col gap-5 bg-unbox-dark-brand-start p-6">
          <DrawerHeader
            onClose={onClose}
            onSaveChat={() => {
              downloadChatTranscript(messages, scope).catch((error) => console.error(error));
            }}
            canSaveChat={hasAnswer}
          />

          {hasThread ? (
            <Thread
              messages={messages}
              onExplore={submit}
              onRephrase={onRephrase}
              disabled={loading}
              dense
              footer={hasAnswer ? <NoMemoryNotice /> : undefined}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <DrawerEmptyState prompts={suggestions} title={emptyStateTitle} onPickPrompt={submit} />
            </div>
          )}

          <DrawerComposer onSubmit={submit} onStop={cancel} loading={loading} />
        </div>
      </AskSurfaceContext.Provider>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "var(--color-unbox-cream-25)",
      }}
    >
      <AppHeader
        scope={scope}
        onNewChat={handleNewChat}
        hasThread={hasThread}
        userInitials={userInitials}
        onOpenGlossary={() => setGlossaryOpen(true)}
      />

      {/* Main body */}
      {hasThread ? (
        <Thread messages={messages} onExplore={submit} onRephrase={onRephrase} disabled={loading} />
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EmptyHero
            scope={scope}
            onPickPrompt={(text) => submit(text)}
            userName={userName}
            userEmail={userEmail}
            composer={
              <Composer
                onSubmit={submit}
                onGenerateReport={generateReport}
                scope={scope}
                disabled={loading}
                reportsDirection="down"
              />
            }
          />
          <div style={{ flex: 1 }} />
        </div>
      )}

      {/* Composer — docked at the bottom once a thread starts */}
      {hasThread && (
        <div
          style={{
            borderTop: "1px solid var(--color-border-secondary)",
            background: "var(--color-bg-secondary)",
          }}
        >
          <Composer
            onSubmit={submit}
            onGenerateReport={generateReport}
            scope={scope}
            disabled={loading}
            value={draft}
            onValueChange={setDraft}
            loading={loading}
            onStop={cancel}
          />
        </div>
      )}

      {/* Glossary slide-over */}
      <GlossaryPanel open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
    </div>
  );
}
